/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, ItemAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors,
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxResources as IBuyboxExtentionResources,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import { getFullAvailableInventoryNearbyAction, GetFullAvailableInventoryNearbyInput } from '../../../actions/get-full-available-inventory-nearby';
import {
    ArrayExtensions,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { IncrementalQuantity, INodeProps, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import React, { useEffect } from 'react';

import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';

import { toJS } from 'mobx';
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {unitOfMeasure}
            {quantityLimitsMessages}
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */

const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps, props: any): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    useEffect(() => {
        const buttonEl = document.querySelector('.msc-add-to-cart');
        const product = props?.data?.product?.result;
        const currency = props.ModuleProps.moduleProps.data.productPrice.result?.CurrencyCode

        const handler = () => {
            const categories = Array.from(
                document.querySelectorAll('.ms-breadcrumb_item a span[itemprop="name"]')
              ).map(span => span.textContent);

          const productInfo = {
            item_id: product.RecordId.toString(),
            item_name: product?.Name,
            price: product.Price || 0,
            discount: product.BasePrice && product.Price
            // @ts-ignore
                ? parseFloat((product.Price - product.BasePrice).toFixed(2))
                : 0,
            // @ts-ignore,
            quantity: 1,
            currency: currency,
            item_list_id: product?.ItemId,
            item_list_name: categories && categories[0],
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: productInfo.currency,
              value: productInfo.price,
              items: [
                {
                  item_id: productInfo.item_id,
                  item_name: productInfo.item_name,
                  discount: productInfo.discount,
                  index: 0,
                  item_category: categories[0] || '',
                  item_category2: categories[1] || '',
                  item_category3: categories[2] || '',
                  item_category4: categories[3] || '',
                  item_list_id: productInfo.item_list_id,
                  item_list_name: productInfo.item_list_name,
                  price: productInfo.price,
                  quantity: productInfo.quantity,
                },
              ],
            },
          });
        };

        if (buttonEl) {
          buttonEl.addEventListener('click', handler);
        }

        return () => {
          if (buttonEl) {
            buttonEl.removeEventListener('click', handler);
          }
        };
      }, []);

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        // @ts-ignore
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs,
        context
    } = props;

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (!window.dataLayer) {
                window.dataLayer = [];
            }
            (window as any).dataLayer.push({ ecommerce: null });
            // @ts-ignore
            const categoryHierarchy = toJS(props?.data?.categoryHierarchy?.result)
            const categories = Array.from(
                document.querySelectorAll('.ms-breadcrumb_item a span[itemprop="name"]')
              ).map(span => span.textContent);
            const product = props?.data?.product?.result;
            // @ts-ignore
            const currency = props.ModuleProps.moduleProps.data.productPrice.result?.CurrencyCode
            const item = {
                // @ts-ignore
                item_id: product.RecordId.toString(),
                // @ts-ignore
                item_name: product?.Name,
                // @ts-ignore
                discount: product.BasePrice && product.Price
                // @ts-ignore
                    ? parseFloat((product.Price - product.BasePrice).toFixed(2))
                    : 0,
                // @ts-ignore
                index: 0,
                // @ts-ignore
                item_category: categories && categories[0] || '',
                // @ts-ignore
                item_category2: categories && categories[1] || '',
                // @ts-ignore
                item_category3: categories && categories[2] || '',
                // @ts-ignore
                item_category4: categories && categories[3] || '',
                item_list_name: categories && categories[3],
                // @ts-ignore
                price: product.Price || 0,
                quantity: 1
            };

            (window as any).dataLayer.push({
                event: 'view_item',
                ecommerce: {
                    currency: currency,
                    // @ts-ignore
                    value: product.BasePrice && product.Price
                    // @ts-ignore
                        ? parseFloat((product.Price - product.BasePrice).toFixed(2))
                        : 0,
                    items: [item]
                }
            });
        }
    }, []);

    // @ts-ignore
    const productAvailability = props?.data?.productAvailableQuantity?.result?.peek?.()?.[0] ?? {};

    // @ts-ignore
    const InventoryLabelWithColors = () => {
        const content =
            productAvailability?.StockLevelLabel?.trim()
                .toLowerCase()
                .replace(/\s+/g, '') ?? 'outofstock';

        const availabilityClasses = {
            available: 'ms-buybox__store-availability-circle ms-buybox__store-availability-circle--green',
            fewavailable: 'ms-buybox__store-availability-circle ms-buybox__store-availability-circle--yellow',
            outofstock: 'ms-buybox__store-availability-circle ms-buybox__store-availability-circle--red',
            notvisible: 'ms-buybox__store-availability-circle ms-buybox__store-availability-circle--gray'
        };

        // @ts-ignore
        const circleClass = availabilityClasses[content] || 'ms-buybox__store-availability-circle ms-buybox__store-availability-circle--red';

        return (
            <div className='ms-buybox__inventory-info-availability ms-buybox__store-availability-tooltip-container'>
                {/*<><span className={circleClass}></span>
                    <span>{productAvailability?.StockLevelLabel?.toLowerCase()}</span>
                    <div className="ms-buybox__store-availability-tooltip">
                        <div>
                            <span
                                className="ms-buybox__store-availability-circle ms-buybox__store-availability-circle--green"></span> available
                        </div>
                        <div>
                            <span
                                className="ms-buybox__store-availability-circle ms-buybox__store-availability-circle--yellow"></span> few
                            available
                        </div>
                        <div>
                            <span
                                className="ms-buybox__store-availability-circle ms-buybox__store-availability-circle--red"></span> out
                            of
                            stock
                        </div>
                    </div>
                </>*/}
            </div>
        );
    };

    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearbyAction(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    const renderPrice = () => {
        // @ts-ignore
        const productPrice = price?.props?.data?.price;

        if (productPrice) {
            const fullPrice = props.context.cultureFormatter.formatCurrency(productPrice.AdjustedPrice, productPrice?.CurrencyCode);
            const discountPrice = props.context.cultureFormatter.formatCurrency(
                productPrice.CustomerContextualPrice,
                productPrice?.CurrencyCode
            );

            if (productPrice.DiscountAmount > 0) {
                return (
                    <span className='msc-price'>
                        <span className='msc-price__strikethrough' aria-hidden='true'>
                            {props.resources.grossLabel + fullPrice}
                        </span>
                        <span aria-hidden='false'>
                            <span className='msc-price__actual' itemProp='price'>
                                {props.resources.netLabel + discountPrice}
                            </span>
                        </span>
                        {renderDiscount()}
                    </span>
                );
            } else {
                return (
                    <span className='msc-price'>
                        <span aria-hidden='false'>
                            <span className='msc-price__actual' itemProp='price'>
                                {props.resources.netLabel + fullPrice}
                            </span>
                        </span>
                    </span>
                );
            }
        }

        return <></>;
    };

    const renderDiscount = () => {
        //@ts-ignore
        const discountPercentage = price?.props?.data?.price?.DiscountLines[0]?.EffectivePercentage.toFixed(2);

        return (
            <div>
                <h2 className='msc-buybox__discount-price-section'>{props.resources.discountLabel + discountPercentage} %</h2>
            </div>
        );
    };

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {catalogs && catalogs[0]?.Name}
                {title}
                <Node className='msc-buybox__ratings-section'>
                    <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                    {rating}
                </Node>
                {context.request.user?.isAuthenticated && (
                    <Node className='msc-buybox__price-section'>
                        <div className='msc-buybox__price-section-text'>{props.resources.priceText}</div>
                        {renderPrice()}
                    </Node>
                )}
                {configure && renderConfigure(configure)}
                {keyInPrice && renderKeyInPrice(keyInPrice)}
                <Node className='msc-buybox__bulk-purchase-section'>
                    <div className='msc-buybox__bulk-purchase-button-text'>
                        {quantity &&
                            renderQuantity(
                                quantity,
                                callbacks,
                                props,
                                state,
                                resources,
                                quantityLimitsMessages,
                                min,
                                max,
                                applyDefaultOrderSettings,
                                telemetryContent,
                                unitOfMeasure
                            )}
                        {props.bulkPurchaseLink}
                    </div>
                </Node>
                {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                {InventoryLabelWithColors()}
                {// @ts-ignore
                context.request.user?.isAuthenticated && renderAddToCart(addToCart,props)}
                {findInStore && renderFindInStore(findInStore)}
                {context.request.user?.isAuthenticated && addToWishlist && renderAddToWishlist(props, state, callbacks)}
                {productComparisonButton}
                {context.request.user?.isAuthenticated && addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                {findInStore?.productPickupOptionList ? (
                    <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                ) : null}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook || shopSimilarDescription ? (
                    <>
                        <div className='msc-buybox__description-text'>{props.resources.shopText}</div>
                        <Node className='msc-buybox__shop-description'>
                            {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                            {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                        </Node>
                    </>
                ) : null}
                <Node className='msc-buybox__description-section'>
                    <div className='msc-buybox__description-section-text'>{props.resources.descriptionText}</div>
                    {description}
                </Node>
                {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                    <>
                        <div className='msc-buybox__description-section-text'>{props.resources.specificationsText}</div>
                        <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
                        {// @ts-ignore
                        typeof document !== 'undefined' &&
                            document?.getElementsByClassName('ms-product-details-additional-media')[0] &&
                            Array.from(
                                document?.getElementsByClassName('ms-product-details-additional-media')[0]?.getElementsByTagName('a') || []
                            )?.forEach((a, i) => {
                                // @ts-ignore
                                const skuText = document.querySelector('.msc-buybox__ratings-section-sku-text')?.innerText;
                                const sku = skuText ? skuText.split('SKU:')[1].trim() : 'UnknownSKU';
                                const filename = `${sku}_${String(i + 1).padStart(3, '0')}.pdf`;
                                a.innerText = filename;
                                a.setAttribute('target', '_blank');
                                a.setAttribute('rel', 'noopener noreferrer');
                            })}
                    </>
                ) : null}
            </Node>
        </Module>
    );
};

export default BuyboxView;
